import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import DictationPunctuationHint from "../../../../shared/ui/common/DictationPunctuationHint";
import { useTherapist } from "../../../../context/useTherapist";
import { insertDictatedText } from "../../../../shared/domain/dictationText";
import { useDictation } from "../../application/useDictation";
import { estimateFromScript } from "../../domain/readingSpeed";
import { formatDuration } from "../../domain/types";

type Props = {
    script: string;
    onChange: (script: string) => void;
    /** Only to say how long the text runs; the pace itself is set elsewhere. */
    readingSpeed: number;
};

/**
 * The text of a meditation, written or dictated.
 *
 * Dictation sits with the textarea rather than in a toolbar: a therapist who
 * would rather say it than type it is doing the same thing, by another route.
 *
 * What they say lands in the field as they say it, at the cursor they left in
 * the script — a meditation is rewritten as often as it is written, and having
 * to dictate an added line at the end and then move it is not dictating it.
 * The half-heard words are shown in that same place and are replaced by the
 * settled phrase the moment the recogniser commits it, so the meditation is
 * written where the therapist is looking rather than accumulating in a hint
 * below it and arriving in bursts.
 *
 * The field is read-only while the microphone is open. Only while: the words
 * on screen are partly unsettled and will be rewritten, and a cursor placed
 * into that would be moved out from under the therapist. For the same reason
 * moving the cursor mid-dictation does not move where the words go — stopping
 * hands the text straight back, with the cursor where the dictation ended.
 */
export default function MeditationScriptSection({
    script,
    onChange,
    readingSpeed,
}: Props) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();

    /**
     * The script as it stands, read at the moment a phrase settles rather than
     * at the render that built the callback: two phrases can settle before the
     * screen catches up — one event that settles two, or two events inside a
     * batch — and the second would otherwise be written over the first.
     */
    const scriptRef = useRef(script);
    useEffect(() => {
        scriptRef.current = script;
    }, [script]);

    const fieldRef = useRef<HTMLTextAreaElement | null>(null);
    /**
     * Where dictated text lands: the cursor the therapist left in the script,
     * moving along with each phrase that settles. Null until they have put a
     * cursor anywhere — dictating into a script nobody has clicked into means
     * carrying on at the end, not writing over the beginning.
     */
    const cursorRef = useRef<number | null>(null);
    const insertionRef = useRef(script.length);

    const dictation = useDictation((phrase) => {
        const { text, caret } = insertDictatedText(
            scriptRef.current,
            insertionRef.current,
            phrase
        );
        scriptRef.current = text;
        insertionRef.current = caret;
        onChange(text);
    }, therapist?.language === "en" ? "en-US" : "es-ES");

    const rememberCursor = () => {
        // While the microphone is open the cursor is ours, not theirs: it
        // follows the words being written and must not be read back as a move.
        if (dictation.listening) return;
        cursorRef.current = fieldRef.current?.selectionStart ?? null;
    };

    const startDictation = () => {
        insertionRef.current = cursorRef.current ?? scriptRef.current.length;
        // A cursor is only drawn in a focused field, and the therapist should
        // be able to see where their words are about to go.
        fieldRef.current?.focus();
        dictation.start();
    };

    const estimate = useMemo(
        () => estimateFromScript(script, readingSpeed),
        [script, readingSpeed]
    );

    /**
     * The script as it stands, with whatever is still being heard written in at
     * the cursor — joined exactly as the settled phrase will be, so the words
     * do not shift when the recogniser commits them.
     *
     * The insertion point is read straight from the ref: every change that
     * moves it also changes the script or the interim text, so the render that
     * follows always reads the value that goes with what it is drawing.
     */
    const heard =
        dictation.listening && dictation.interim
            ? insertDictatedText(script, insertionRef.current, dictation.interim)
            : null;
    const live = heard?.text ?? script;

    // Long meditations run past the bottom of the field, and dictation can be
    // happening anywhere in them; putting the cursor back where the words are
    // landing is what keeps them in sight.
    const wasListeningRef = useRef(false);
    useEffect(() => {
        const stoppedJustNow = wasListeningRef.current && !dictation.listening;
        wasListeningRef.current = dictation.listening;

        const field = fieldRef.current;
        // Outside dictation the cursor belongs to the therapist: moving it
        // while they type would fight them.
        if (!field || (!dictation.listening && !stoppedJustNow)) return;

        const at = Math.min(heard?.caret ?? insertionRef.current, live.length);
        field.setSelectionRange(at, at);
        // `heard` is deliberately not a dependency: it is rebuilt every render
        // from the text this already watches.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, dictation.listening]);

    return (
        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="meditation-script" className="mb-0">
                    {t("meditation.fields.script")}
                </Label>
                {dictation.supported && (
                    <div className="flex items-center gap-1">
                        <DictationPunctuationHint
                            language={therapist?.language === "en" ? "en" : "es"}
                        />
                        <Button
                            size="sm"
                            variant={dictation.listening ? "destructive" : "outline"}
                            startIcon={
                                dictation.listening ? <MicOff size={15} /> : <Mic size={15} />
                            }
                            onClick={dictation.listening ? dictation.stop : startDictation}
                        >
                            {t(
                                dictation.listening
                                    ? "meditation.dictation.stop"
                                    : "meditation.dictation.start"
                            )}
                        </Button>
                    </div>
                )}
            </div>

            <textarea
                ref={fieldRef}
                id="meditation-script"
                rows={14}
                className="field-base field-default w-full px-4 py-2.5 leading-relaxed"
                value={live}
                readOnly={dictation.listening}
                onChange={(e) => onChange(e.target.value)}
                onSelect={rememberCursor}
                onFocus={rememberCursor}
                placeholder={t("meditation.fields.scriptPlaceholder")}
            />

            {dictation.listening && (
                <p
                    role="status"
                    className="mt-2 flex items-center gap-2 text-sm text-content-tertiary"
                >
                    <span
                        aria-hidden="true"
                        className="size-2 animate-pulse rounded-full bg-status-critical-fill"
                    />
                    {t("meditation.dictation.listening")}
                </p>
            )}
            {dictation.error && (
                <p role="alert" className="mt-2 text-sm text-status-critical">
                    {t(`meditation.dictation.errors.${dictation.error}`)}
                </p>
            )}

            {/*
              * The tags are part of the script, so what they are is written
              * where the script is: a therapist who never opens the
              * documentation should still be able to leave a breath.
              *
              * Two entries, because there are two tags. A legend that listed
              * things the prompter does not act on would be a promise nobody
              * keeps.
              */}
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-content-tertiary">
                {[
                    { tag: "[10s]", key: "pause" },
                    { tag: "[gong]", key: "gong" },
                ].map(({ tag, key }) => (
                    <div key={key} className="flex items-center gap-2">
                        <dt className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-content-secondary">
                            {tag}
                        </dt>
                        <dd>{t(`meditation.fields.tags.${key}`)}</dd>
                    </div>
                ))}
            </dl>

            <p className="mt-1 text-xs text-content-tertiary">
                {t("meditation.fields.scriptEstimate", {
                    words: estimate.words,
                    duration: formatDuration(estimate.seconds),
                })}
            </p>
        </section>
    );
}
